import {useState} from 'react'
import {Link, NavLink, Outlet} from 'react-router-dom'
import {BRAND, CONTACT, LOCATIONS, NAV_LINKS} from './site'
import {LightboxProvider} from './Lightbox'
import {useAuth} from '../../modules/auth'
import './public.css'

const ACCOUNT_NAV = [
  {to: '/home', label: 'Dashboard'},
  {to: '/booking', label: 'My booking'},
  {to: '/payments', label: 'Payments'},
  {to: '/requests', label: 'Cleaning & Maintenance'},
  {to: '/announcements', label: 'Announcements'},
  {to: '/profile', label: 'Profile'},
]

/**
 * Chrome for the whole guest + renter site — a sticky top nav with the brand and
 * the marketing links. Guests get a "Sign In" CTA; signed-in renters get an
 * avatar + name button that opens a right-hand account drawer (nav + sign-out).
 * Both audiences browse the same public pages; renters also reach their
 * dashboard pages through this layout.
 */
/** A real map-pin icon (stroke follows currentColor) — replaces the 📍 emoji. */
const MapPin = ({size = 16}: {size?: number}) => (
  <svg width={size} height={size} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' style={{flexShrink: 0}}>
    <path d='M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z' />
    <circle cx='12' cy='10' r='3' />
  </svg>
)

const PublicLayout = () => {
  const {currentUser} = useAuth()
  const [open, setOpen] = useState(false)
  const [drawer, setDrawer] = useState(false)
  const [locOpen, setLocOpen] = useState(false)
  const activeLocation = LOCATIONS.find((l) => l.active) ?? LOCATIONS[0]

  const name = [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' ') || 'Crew member'
  const initials =
    [currentUser?.first_name, currentUser?.last_name]
      .filter(Boolean)
      .map((n) => n![0]?.toUpperCase())
      .join('') || 'CC'

  const avatarCircle = (size: number, fontSize: number) => (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${BRAND.green}, ${BRAND.greenDark})`,
        color: '#fff',
        fontWeight: 700,
        fontSize,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        letterSpacing: '0.02em',
      }}
    >
      {initials}
    </span>
  )

  return (
    <LightboxProvider>
    <div style={{minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#fff', overflowX: 'clip', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Inter, sans-serif'}}>
      {/* Header */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'saturate(180%) blur(8px)',
          borderBottom: '1px solid #eef0f4',
        }}
      >
        <div style={{maxWidth: 1140, margin: '0 auto', padding: '0 20px', height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <Link to='/' style={{display: 'flex', alignItems: 'center', textDecoration: 'none'}}>
            <img src='/logo.png' alt='Crosscheck CrashPad' style={{height: 42, display: 'block'}} />
          </Link>

          {/* Desktop nav */}
          <nav className='d-none d-lg-flex' style={{alignItems: 'center', gap: 28}}>
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                style={({isActive}) => ({
                  textDecoration: 'none',
                  fontSize: 15,
                  fontWeight: 600,
                  color: isActive ? BRAND.green : '#475569',
                })}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
            {/* Location selector — SFO is live; others are visible but not reservable */}
            <div className='d-none d-md-block' style={{position: 'relative'}}>
              <button
                type='button'
                onClick={() => setLocOpen((v) => !v)}
                aria-haspopup='listbox'
                aria-expanded={locOpen}
                style={{display: 'flex', alignItems: 'center', gap: 7, border: '1px solid #e5e7eb', borderRadius: 999, padding: '7px 12px', fontSize: 13, fontWeight: 600, color: BRAND.ink, background: '#fff', cursor: 'pointer'}}
              >
                <span style={{color: BRAND.green, display: 'inline-flex'}}><MapPin /></span>
                {activeLocation.label}
                <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='#94a3b8' strokeWidth='2.5' style={{transform: locOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s'}}>
                  <polyline points='6 9 12 15 18 9' />
                </svg>
              </button>
              {locOpen && (
                <>
                  <div onClick={() => setLocOpen(false)} style={{position: 'fixed', inset: 0, zIndex: 60}} />
                  <ul role='listbox' style={{position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 61, minWidth: 250, listStyle: 'none', margin: 0, padding: 6, background: '#fff', border: '1px solid #eef0f4', borderRadius: 12, boxShadow: '0 12px 32px rgba(15,23,42,0.16)'}}>
                    {LOCATIONS.map((l) => {
                      const isActive = l.code === activeLocation.code
                      return (
                        <li
                          key={l.code}
                          role='option'
                          aria-selected={isActive}
                          aria-disabled={!l.active}
                          onClick={() => l.active && setLocOpen(false)}
                          style={{display: 'flex', alignItems: 'center', gap: 9, padding: '9px 11px', borderRadius: 9, fontSize: 14, fontWeight: 600, cursor: l.active ? 'pointer' : 'default', color: l.active ? BRAND.ink : BRAND.muted, background: isActive ? '#f0faf4' : 'transparent'}}
                        >
                          <span style={{color: l.active ? BRAND.green : '#c2c8d4', display: 'inline-flex'}}><MapPin /></span>
                          <span style={{flex: 1, whiteSpace: 'nowrap'}}>{l.label}</span>
                          {isActive ? (
                            <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke={BRAND.green} strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'><polyline points='20 6 9 17 4 12' /></svg>
                          ) : !l.active ? (
                            <span style={{fontSize: 11, fontWeight: 700, color: BRAND.muted, background: '#f1f3f7', borderRadius: 999, padding: '2px 8px'}}>Coming soon</span>
                          ) : null}
                        </li>
                      )
                    })}
                  </ul>
                </>
              )}
            </div>
            {currentUser ? (
              /* Avatar + name → opens the account drawer */
              <button
                onClick={() => setDrawer(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 999,
                  padding: '5px 14px 5px 5px',
                  cursor: 'pointer',
                }}
              >
                {avatarCircle(34, 14)}
                <span className='d-none d-sm-inline' style={{fontWeight: 600, fontSize: 14, color: BRAND.ink}}>{name}</span>
                <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='#94a3b8' strokeWidth='2.5' style={{flexShrink: 0}}>
                  <polyline points='6 9 12 15 18 9' />
                </svg>
              </button>
            ) : (
              <Link
                to='/auth/login'
                className='d-none d-lg-flex'
                style={{textDecoration: 'none', fontWeight: 700, fontSize: 15, color: '#fff', background: BRAND.green, borderRadius: 10, padding: '9px 18px'}}
              >
                Sign In
              </Link>
            )}

            {/* Mobile toggle */}
            <button
              className='d-lg-none'
              onClick={() => setOpen((v) => !v)}
              aria-label='Menu'
              style={{background: 'transparent', border: 'none', fontSize: 24, color: BRAND.ink, cursor: 'pointer'}}
            >
              ☰
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className='d-lg-none' style={{borderTop: '1px solid #eef0f4', padding: '12px 20px', background: '#fff'}}>
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                onClick={() => setOpen(false)}
                style={({isActive}) => ({display: 'block', padding: '10px 0', textDecoration: 'none', fontWeight: 600, color: isActive ? BRAND.green : '#475569'})}
              >
                {l.label}
              </NavLink>
            ))}
            <div style={{borderTop: '1px solid #eef0f4', marginTop: 8, paddingTop: 10}}>
              <div style={{fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: BRAND.muted, marginBottom: 6}}>Locations</div>
              {LOCATIONS.map((l) => (
                <div key={l.code} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', fontSize: 14, fontWeight: 600, color: l.active ? BRAND.ink : BRAND.muted}}>
                  <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}>
                    <span style={{color: l.active ? BRAND.green : '#c2c8d4', display: 'inline-flex'}}><MapPin /></span>
                    {l.label}
                  </span>
                  {!l.active && <span style={{fontSize: 11, fontWeight: 700, color: BRAND.muted, background: '#f1f3f7', borderRadius: 999, padding: '2px 8px'}}>Coming soon</span>}
                </div>
              ))}
            </div>
            {!currentUser && (
              <div style={{display: 'flex', gap: 10, marginTop: 8}}>
                <Link to='/auth/login' onClick={() => setOpen(false)} style={{flex: 1, textAlign: 'center', textDecoration: 'none', fontWeight: 700, color: '#fff', background: BRAND.green, borderRadius: 10, padding: '9px 0'}}>
                  Sign In
                </Link>
              </div>
            )}
          </div>
        )}
      </header>

      {/* Tap-outside backdrop to close the mobile nav menu */}
      {open && (
        <div
          className='d-lg-none'
          onClick={() => setOpen(false)}
          style={{position: 'fixed', inset: 0, zIndex: 40}}
        />
      )}

      {/* Account drawer + backdrop (signed-in users) */}
      {drawer && currentUser && (
        <div
          onClick={() => setDrawer(false)}
          style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 100, animation: 'ccpFade 0.18s ease'}}
        >
          <aside
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              height: '100%',
              width: 320,
              maxWidth: '85vw',
              background: '#fff',
              boxShadow: '-12px 0 40px rgba(15,23,42,0.18)',
              display: 'flex',
              flexDirection: 'column',
              animation: 'ccpSlideIn 0.22s ease',
            }}
          >
            <div style={{padding: '24px 22px', borderBottom: '1px solid #eef0f4', display: 'flex', alignItems: 'center', gap: 14}}>
              {avatarCircle(48, 18)}
              <div style={{minWidth: 0}}>
                <div style={{fontWeight: 700, fontSize: 15, color: BRAND.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{name}</div>
                {currentUser?.email && (
                  <div style={{fontSize: 13, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{currentUser.email}</div>
                )}
              </div>
              <button
                onClick={() => setDrawer(false)}
                aria-label='Close'
                style={{marginLeft: 'auto', background: 'transparent', border: 'none', fontSize: 22, lineHeight: 1, color: '#94a3b8', cursor: 'pointer'}}
              >
                ×
              </button>
            </div>

            <nav style={{padding: '12px 12px', flex: 1}}>
              {ACCOUNT_NAV.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setDrawer(false)}
                  style={({isActive}) => ({
                    display: 'block',
                    padding: '12px 14px',
                    borderRadius: 10,
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: 15,
                    marginBottom: 4,
                    color: isActive ? BRAND.green : '#334155',
                    background: isActive ? '#f0faf4' : 'transparent',
                  })}
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>

            <div style={{padding: '16px 18px', borderTop: '1px solid #eef0f4'}}>
              <Link
                to='/logout'
                onClick={() => setDrawer(false)}
                style={{
                  display: 'block',
                  textAlign: 'center',
                  padding: '11px 0',
                  borderRadius: 10,
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: 15,
                  color: '#dc2626',
                  border: '1px solid #fecaca',
                  background: '#fef2f2',
                }}
              >
                Sign out
              </Link>
            </div>
          </aside>
        </div>
      )}

      <main style={{flex: 1}}>
        <Outlet />
      </main>

      {/* Footer */}
      <footer style={{background: BRAND.ink, color: '#cbd2e0', marginTop: 'auto'}}>
        <div style={{maxWidth: 1140, margin: '0 auto', padding: '48px 20px 24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 32}}>
          <div>
            <img src='/logo-white.png' alt='Crosscheck CrashPad' style={{height: 40, display: 'block', marginBottom: 14}} />
            <p style={{fontSize: 14, lineHeight: 1.6, margin: 0}}>
              A safe, co-ed, verified crashpad for aviation professionals — one shared space in San Francisco, convenient to and from SFO.
            </p>
          </div>

          <div>
            <h4 style={{color: '#fff', fontSize: 14, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 14}}>Explore</h4>
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} style={{display: 'block', color: '#cbd2e0', textDecoration: 'none', fontSize: 14, padding: '5px 0'}}>
                {l.label}
              </Link>
            ))}
          </div>

          <div>
            <h4 style={{color: '#fff', fontSize: 14, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 14}}>Info</h4>
            {[
              {to: '/info/transport-guide', label: 'Getting To & From SFO'},
              {to: '/info/house-rules', label: 'House guidelines'},
              {to: '/info/policies', label: 'Policies'},
              {to: '/info/permanent-departure', label: 'Permanent departure'},
            ].map((l) => (
              <Link key={l.to} to={l.to} style={{display: 'block', color: '#cbd2e0', textDecoration: 'none', fontSize: 14, padding: '5px 0'}}>
                {l.label}
              </Link>
            ))}
          </div>

          <div>
            <h4 style={{color: '#fff', fontSize: 14, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 14}}>Contact</h4>
            <p style={{fontSize: 14, lineHeight: 1.7, margin: 0}}>
              {CONTACT.publicLocation}
              <br />
              Phone: {CONTACT.phone ?? 'Coming soon'}
              <br />
              Email: {CONTACT.email ?? 'Coming soon'}
            </p>
          </div>

          <div>
            <h4 style={{color: '#fff', fontSize: 14, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 14}}>Reservations</h4>
            <p style={{fontSize: 14, lineHeight: 1.7, margin: 0}}>
              {CONTACT.reserveNote}
            </p>
          </div>
        </div>
        <div style={{borderTop: '1px solid rgba(255,255,255,0.1)', padding: '18px 20px', textAlign: 'center', fontSize: 13, color: '#8b93a7'}}>
          © {new Date().getFullYear()} Crosscheck CrashPad. All rights reserved.
        </div>
      </footer>

      <style>{`
        @keyframes ccpFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes ccpSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>
    </div>
    </LightboxProvider>
  )
}

export {PublicLayout}
